package com.corbettcode.allaboutrocks.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initAboutPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("About.md", FrontMatterElement.Builder { addScalar("title", "About"); }))
}

@Page("/about")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun AboutPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("about-all-about-rocks") }) {
        org.jetbrains.compose.web.dom.Text("About All About Rocks")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("All About Rocks is a reference for the rocks and minerals that rockhounds actually pick up — written for someone holding a stone and wondering what it is, rather than for someone studying for a geology exam.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The rock types themselves are the same everywhere, so most of what is here applies wherever you collect. The rock pages lean toward the Pacific Northwest, because that is where the beaches, gravel bars, and thunderegg beds behind most of them are. The places worth travelling for cast a wider net, and cover the country.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-s-here") }) {
        org.jetbrains.compose.web.dom.Text("What's Here")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Types of Rocks")
        }
        org.jetbrains.compose.web.dom.Text(" covers the three classes every rock belongs to — igneous, born from cooling melt; sedimentary, laid down grain by grain at the surface; and metamorphic, cooked and squeezed into something new without ever melting. The Rock Cycle page ties the three together, because none of them is a final state: given enough time, heat, and pressure, any one of them becomes another. Alongside the three classes sit the stones that most collecting actually revolves around, which are nearly all quartz in one form or another — quartz itself, chalcedony, agate, jasper, carnelian, and petrified wood — with epidote the odd one out. Each gets its own page covering how it forms, how to recognize it, where it turns up, and what it is like to cut and polish.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Learn")
        }
        org.jetbrains.compose.web.dom.Text(" is the practical side. How to identify what you have found, how to run a tumbler without turning good material into gravel, and which books are worth owning.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Awesome Places")
        }
        org.jetbrains.compose.web.dom.Text(" is twenty collecting sites worth the drive, grouped by region — mostly public land where anyone can fill a bucket, plus a few long-running fee digs famous enough to earn their admission. Each entry says what turns up there and what kind of land it is. None of them quote a fee or a season, because those change; check with whoever manages the ground before you go. There is a form at the bottom of the page for suggesting somewhere that belongs on the list.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Resources")
        }
        org.jetbrains.compose.web.dom.Text(" is everything that is not a rock: the rock and mineral clubs, Facebook groups, YouTube channels, and reference sites worth keeping to hand. Clubs are the fastest way to learn this hobby, and most of them welcome visitors long before you join.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Contact Us")
        }
        org.jetbrains.compose.web.dom.Text(" opens a message straight to the site's inbox. Corrections are especially welcome; see below for why.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("before-you-collect") }) {
        org.jetbrains.compose.web.dom.Text("Before You Collect")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every page here that mentions where to find something also mentions checking the rules first, and it is worth saying once in one place: beaches, BLM ground, national forest, state parks, and private land each have their own limits on what you may collect and how much. Some allow a reasonable amount for personal use, some allow nothing at all, and the boundaries are rarely marked on the ground. Find out before you fill a bucket.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("built-with-claude") }) {
        org.jetbrains.compose.web.dom.Text("Built with Claude")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This site is written in Kotlin using ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://kobweb.varabyte.com") {
            org.jetbrains.compose.web.dom.Text("Kobweb")
        }
        org.jetbrains.compose.web.dom.Text(", a framework that lets you build a web frontend with Compose. There is no server behind it — the whole thing exports to static files.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Much of it was built with Claude Code, and it seems worth being straightforward about that. Claude wrote site components such as the rotating carousel on the home page, scaffolded new rock pages from the structure of existing ones, worked through the CSS-in-Kotlin that Kobweb uses, and produced first drafts of the rock write-ups. That work is reviewed and corrected before it ships, and the geology here is checked against sources rather than taken on trust.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That last part is not a formality. A language model will produce fluent, confident, entirely invented detail if you let it — a book's rating, a review, a specific locality — and it looks exactly like the parts it gets right. So the rule on this site is that anything checkable gets checked, and anything that cannot be checked does not get written. The book recommendations are a good example: those are real opinions about books actually read, written by hand, because they are precisely the kind of thing a model would be happy to fabricate.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The honest summary is that Claude is very good at the shape of things — structure, layout, boilerplate, a solid first draft — and should not be trusted with the facts. Treated that way, it is a genuinely useful collaborator.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("get-in-touch") }) {
        org.jetbrains.compose.web.dom.Text("Get in Touch")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Found an error, disagree with an identification, or know a locality worth adding? Use the ")
        com.varabyte.kobweb.silk.components.navigation.Link("/contact-us") {
            org.jetbrains.compose.web.dom.Text("Contact Us")
        }
        org.jetbrains.compose.web.dom.Text(" page, or find us in the ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://www.facebook.com/groups/1102691563139199") {
            org.jetbrains.compose.web.dom.Text("Facebook group")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
